import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Crosshair, Megaphone, MessageSquare, Gift, Footprints, RefreshCw,
  Users, Layers, Sparkles, Activity, BarChart3, Target, Eye, Check
} from "lucide-react";
import { FootTrafficAuditForm } from "../components/FootTrafficAuditForm";
import { SEO } from "../components/SEO";

const STEPS = [
  {
    num: "01",
    icon: Crosshair,
    title: "Audience Identification",
    desc: "We define exactly who to reach — combining hyperlocal radius targeting (e.g., 3-mile drive radius around your store), behavioral signals (foot-traffic patterns, visit history), and demographic filters to build audiences that can actually convert into customers.",
    bullets: ["Hyperlocal & behavioral targeting", "Visit-history audiences", "Competitor conquest segments"]
  },
  {
    num: "02",
    icon: Megaphone,
    title: "Campaign Activation",
    desc: "Your campaign launches across the right mix of channels — radio, mobile, social, CTV, and DOOH — orchestrated as one journey instead of disconnected silos. We unify creative, frequency, and dayparting across the entire mix.",
    bullets: ["Multi-channel deployment", "Unified creative system", "Daypart orchestration"]
  },
  {
    num: "03",
    icon: MessageSquare,
    title: "Engagement",
    desc: "Customers see locally relevant, time-sensitive messaging — not generic ads. Creative is tuned to dayparts, neighborhoods, and seasonal moments to feel personal and urgent.",
    bullets: ["Locally relevant creative", "Time-based offers", "Personalization signals"]
  },
  {
    num: "04",
    icon: Gift,
    title: "Incentivization",
    desc: "The dual loyalty system gives customers a real reason to act now and come back later. Points earned on radio + digital can be redeemed in-store, completing the loop.",
    bullets: ["Dual loyalty rewards", "Redemption mechanics", "Tier progression"]
  },
  {
    num: "05",
    icon: Footprints,
    title: "Conversion (Store Visits)",
    desc: "The customer walks through your door — and we know it. Anonymized location signals confirm visits, attribute them back to specific channels and creatives, and measure cost-per-visit.",
    bullets: ["Visit attribution", "Cost-per-visit tracking", "Channel breakdown"]
  },
  {
    num: "06",
    icon: RefreshCw,
    title: "Optimization",
    desc: "Performance data feeds a continuous optimization loop. Budgets shift toward the channels, creatives, and audiences driving the most visits. Underperformers get cut. The system gets smarter every week.",
    bullets: ["Auto-budget allocation", "Creative iteration", "Audience refinement"]
  }
];

const HowItWorksPage = () => {
  const navigate = useNavigate();
  return (
    <div className="pt-20" data-testid="how-it-works-page">
      <SEO
        title="How SmartReach™ Works"
        description="The 6-step SmartReach™ process: Audience Identification, Campaign Activation, Engagement, Incentivization, Conversion, and Optimization — built to drive real customer visits."
        path="/how-it-works"
      />

      <section className="section-padding bg-gradient-to-br from-orange-light via-white to-white">
        <div className="max-w-7xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 text-orange-primary font-semibold text-sm mb-4 bg-white px-4 py-2 rounded-full shadow-sm">
            <Sparkles size={16} /> The SmartReach™ Process
          </span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-gray-dark mb-6 leading-tight">
            How We <span className="text-gradient-orange">Drive Customers</span> To Your Door
          </h1>
          <p className="text-lg md:text-xl text-gray-medium mb-8 max-w-3xl mx-auto">
            Six steps from audience to visit — engineered for measurable, repeatable foot-traffic growth.
          </p>
          <button onClick={() => navigate("/contact")} className="btn-primary text-lg" data-testid="hiw-cta-top">
            Launch Campaign <ArrowRight size={20} className="inline ml-2" />
          </button>
        </div>
      </section>

      {/* STEPS */}
      <section className="section-padding bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="space-y-6">
            {STEPS.map((step, idx) => (
              <div key={idx} className="bg-white p-6 md:p-8 rounded-2xl shadow-card border border-gray-100 card-hover" data-testid={`hiw-step-${idx}`}>
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex items-center gap-4 md:flex-col md:items-center md:justify-start md:min-w-[120px]">
                    <span className="text-5xl md:text-6xl font-display font-bold text-orange-primary/20 leading-none">{step.num}</span>
                    <div className="w-14 h-14 rounded-xl gradient-orange flex items-center justify-center">
                      <step.icon className="text-white" size={28} />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display font-bold text-2xl text-gray-dark mb-3">{step.title}</h3>
                    <p className="text-gray-medium leading-relaxed mb-4">{step.desc}</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      {step.bullets.map((b, i) => (
                        <div key={i} className="flex items-center gap-2 bg-gray-light rounded-lg px-3 py-2">
                          <Check size={14} className="text-orange-primary flex-shrink-0" />
                          <span className="text-sm text-gray-dark">{b}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY IT WORKS */}
      <section className="section-padding bg-gray-light">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              Why The Process <span className="text-gradient-orange">Actually Works</span>
            </h2>
            <p className="text-gray-medium max-w-2xl mx-auto text-lg">
              Three principles separate SmartReach&trade; from typical ad campaigns.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Target, title: "Outcome-First", desc: "Every step is built and measured against a real-world business outcome — not impressions or clicks." },
              { icon: Layers, title: "Channels As One", desc: "Channels reinforce each other instead of competing for budget. The whole is greater than the sum." },
              { icon: RefreshCw, title: "Always Optimizing", desc: "Continuous learning loops mean campaigns get more efficient over time, not less." }
            ].map((p, idx) => (
              <div key={idx} className="bg-white p-6 rounded-2xl shadow-card card-hover">
                <p.icon className="text-orange-primary mb-3" size={32} />
                <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{p.title}</h3>
                <p className="text-gray-medium text-sm">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FootTrafficAuditForm
        title="Want this process applied to your business?"
        subtitle="Get a custom SmartReach™ plan — built for your locations, audience, and goals — in 24 hours."
        variant="dark"
      />
    </div>
  );
};

export default HowItWorksPage;
